/**
 * The seam between the pure stage and the workspace's document write path.
 *
 * `stageFor` is a pure function of the entity, called in the host's render body, and that is the
 * right shape for it — which means the stage can mount `DocumentEditor` while holding neither
 * `mutate`, nor a conversation id, nor anything else only the workspace owns. So the save function
 * travels the other way: the workspace registers it here ONCE (in an effect, torn down with it), and
 * the editor reads it back through `useSyncExternalStore`. Same module-store shape as `stageSignals`,
 * for the same reason: a render path needing synchronous access to a fact that lives outside the
 * render tree.
 *
 * UNREGISTERED = READ-ONLY, NEVER BROKEN. The editor's first render happens before the workspace's
 * register effect runs (child effects fire first), and the editor can outlive an unregister. In both
 * states it must still draw the document and refuse edits — `DocumentEditor` warns once and disables
 * editing while the read returns null. A crash, or a keystroke accepted with nowhere to go, are the
 * two failures this contract exists to rule out.
 */

export interface DocEditPatch {
  /** The document's markdown — the whole body, never a delta. */
  content: string;
  /** The card's DISPLAY title. `updateDocIn` owns the rule for who wins (typed vs derived). */
  title: string;
}

export interface DocEditBus {
  /**
   * Persist one document card's edit everywhere it lives. `docId` is the CARD id (`BirtvyDoc.id`, a
   * number) — resolving it to the Library row (`documentId`, a string UUID) is the registered save's
   * job, because only the workspace can see the card. The two id spaces do not overlap: a row id
   * handed in here would "save" against a card that does not exist, quietly writing nothing.
   */
  save: (docId: number, patch: DocEditPatch) => void;
}

let bus: DocEditBus | null = null;
const listeners = new Set<() => void>();

function bump(): void {
  for (const listener of listeners) listener();
}

/**
 * Register the workspace's save; returns the matching unregister. The unregister clears only ITS OWN
 * registration — StrictMode mounts run register → unregister → register, and a cleanup that cleared
 * unconditionally would kill the second registration a frame after it landed, leaving a workspace
 * that believes it is wired and an editor that is silently read-only.
 */
export function registerDocEditBus(next: DocEditBus): () => void {
  bus = next;
  bump();
  return () => {
    if (bus === next) {
      bus = null;
      bump();
    }
  };
}

/** `useSyncExternalStore`'s snapshot: the bus itself — identity-stable between registrations. */
export function readDocEditBus(): DocEditBus | null {
  return bus;
}

/** `useSyncExternalStore`'s subscribe half. */
export function subscribeDocEditBus(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** The server has no workspace to register one, so its editor is read-only by construction. */
export function serverDocEditBus(): null {
  return null;
}
